import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, Check, Loader2, Search } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

type LiveTenant = { id: string; name: string; is_demo?: boolean };
const FILTER_THRESHOLD = 8;

// Platform owner: every live tenant, sorted by name (F-13 switch path, no grant).
export function OwnerTenantList({ onClose }: { onClose: () => void }) {
    const { t, i18n } = useTranslation();
    const { activeTenantId, switchTenant } = useAuth();
    const nav = useNavigate();
    const [tenants, setTenants] = useState<LiveTenant[] | null>(null);
    const [q, setQ] = useState("");
    const [busyId, setBusyId] = useState<string | null>(null);

    useEffect(() => {
        api.get("/platform/tenants")
            .then((r) => setTenants(((r.data?.items || []) as any[])
                .filter((x) => !x.deleted_at)
                .map((x) => ({ id: x.id, name: x.name || x.id.slice(0, 8), is_demo: !!x.is_demo }))
                .sort((a, b) => a.name.localeCompare(b.name, i18n.language))))
            .catch((e) => { setTenants([]); toast.error(formatApiError(e)); });
    }, [i18n.language]);

    const shown = useMemo(() => {
        const needle = q.trim().toLocaleLowerCase();
        return (tenants || []).filter((x) => !needle || x.name.toLocaleLowerCase().includes(needle));
    }, [tenants, q]);

    const pick = async (id: string) => {
        if (busyId) return;
        setBusyId(id);
        try {
            await switchTenant(id);
            onClose();
            nav("/dashboard");
        } catch (e: any) {
            toast.error(`${t("no_tenant.switch_failed")} ${formatApiError(e)}`);
        } finally { setBusyId(null); }
    };

    if (tenants === null) {
        return <div className="px-3 py-3 flex justify-center" data-testid="tenant-menu-loading"><Loader2 size={16} className="animate-spin" style={{ color: "var(--text-muted)" }} /></div>;
    }
    if (tenants.length === 0) {
        return <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }} data-testid="tenant-menu-empty">{t("topbar.no_live_tenants")}</div>;
    }
    return (
        <div data-testid="tenant-menu-owner-list">
            {tenants.length > FILTER_THRESHOLD && (
                <div className="px-2 py-2 border-b" style={{ borderColor: "var(--border)" }}>
                    <div className="relative">
                        <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="tenant-menu-filter"
                            placeholder={t("topbar.tenant_filter_placeholder")}
                            className="w-full rounded-md pl-7 pr-2 py-1.5 text-sm outline-none border"
                            style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                    </div>
                </div>
            )}
            <div className="max-h-72 overflow-y-auto">
                {shown.length === 0 && (
                    <div className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }} data-testid="tenant-menu-no-match">{t("topbar.tenant_filter_no_match")}</div>
                )}
                {shown.map((x) => (
                    <button key={x.id} data-testid={`tenant-option-${x.id}`} disabled={!!busyId} onClick={() => pick(x.id)}
                        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[var(--selected-bg)] text-sm text-left disabled:opacity-60"
                        style={{ color: "var(--text)" }}>
                        {busyId === x.id ? <Loader2 size={14} className="animate-spin" /> : <Building2 size={14} style={{ color: "var(--text-muted)" }} />}
                        <span className="flex-1 truncate">{x.name}{x.is_demo && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-md" style={{ background: "var(--warning-bg)", color: "var(--warning-fg)" }}>DEMO</span>}</span>
                        {x.id === activeTenantId && <Check size={14} style={{ color: "var(--primary)" }} />}
                    </button>
                ))}
            </div>
        </div>
    );
}
